import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { getDiagnosisOption } from "../../../../../services/GeneralService";
import {
  postSaveVisit,
  postUpdateVisit,
} from "../../../../../services/AssesmentService";
import { getActiveVisitSingle } from "../../../../../services/ActiveVisitService";
import { putUpdateReservationStatus } from "../../../../../services/Handlers/ReservationService";

import TitlePage from "../../../../components/TitlePage";
import Button from "../../../../components/Button";
import { useLocalization } from "../../../../../context/LocalizeContextProvider";
import { useAuth } from "../../../../../context/AuthProvider";
import { useNotification } from "../../../../../context/NotificationProvider";
import { usePushNotifications } from "../../../../../context/PushNotificationProvider";
import {
  roleUser,
  statusReservationType,
} from "../../../../../utils/constants";
import { getParams, retrieveUrlFile } from "../../../../../utils/utils";
import { extractPatientGender } from "../../../../../utils/extractPatientGender";
import PatientDataCard from "../../../../components/ModalDetailReservationV2/components/PatientDataCard";
import TabMenu from "../../../../components/TabMenu";
import Spinner from "../../../../components/Spinner";
import Modal from "../../../../components/Modal";
import VitalSign from "../../../../components/VitalSign";
import { isMobile } from "react-device-detect";

import DoctorAssesment from "./DoctorAssesment";
import NurseAssesment from "./NurseAssesment";
import PharmacyAssesment from "./PharmacyAssesment";
import CpptSummary from "./CpptSummary";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal";
import useBeforeUnload from "../../../../../hooks/useBeforeUnload";
import {
  getAssessment,
  getVitalSigns,
  postAssessment,
  postVitalSigns,
} from "../../../../../services/Handlers/AssesmentService";
import moment from "moment";

export default function AssesmentComponent() {
  const { refetchPharmacyCount } = usePushNotifications();
  const { role, userDetail } = useAuth();
  const notification = useNotification();
  const localize = useLocalization();
  const history = useHistory();
  const location = useLocation();
  const { state } = location;
  const id = getParams("id");
  const patientId = getParams("patientId");

  const defaultInitialState = {
    anamnesia: "",
    examination: "",
    treatment: "",
    riwayatPenyakit: "",
    riwayatPenyakitKeluarga: "",
    routineMedications: "",
    diagnosis: [],
    allergies: [],
    fileUploads: [],
    patientId: null,
    satuSehatEncounterId: null,
    practitionerRole: [],
    updatedBy: "",
    updatedAt: "",
  };

  const defaultAssesmentState = {
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
    // Doctor API fields that nurse can also edit
    allergies: [],
    historyOfIllness: "",
    familyHistoryOfDisease: "",
    routineMedications: "",
    fileUploads: [],

    updatedBy: "",
    updatedAt: "",
  };

  const isNurse =
    role === roleUser.ROLE_NURSE || role === roleUser.ROLE_BEAUTICIAN;
  const isDoctor = role === roleUser.ROLE_DOCTOR;
  const isPharmacy = role === roleUser.ROLE_PHARMACY;
  const isOwner = role === roleUser.ROLE_OWNER;

  const isViewFromState = state?.isView || role === roleUser.ROLE_STAFF;

  const getInitialActiveMenu = () => {
    if (isDoctor) return "doctor";
    if (isNurse) return "nurse";
    if (isPharmacy) return "pharmacy";
    if (isOwner) return "doctor";
    return "doctor";
  };

  const [initialValues, setInitialValues] = useState(defaultInitialState);
  const [assesmentFormData, setAssesmentFormData] = useState({
    nurse: { ...defaultAssesmentState },
    pharmacy: { ...defaultAssesmentState },
  });
  const [detailPatient, setDetailPatient] = useState(null);
  const [activeMenuAssesment, setActiveMenuAssesment] = useState(
    getInitialActiveMenu()
  );
  const [showVitalSign, setShowVitalSign] = useState(false);
  const [annotationData, setAnnotationData] = useState([]);
  const [patientGender, setPatientGender] = useState(null);
  // Unsaved-changes guard state. `isDirty` flips to true on any child form
  // change and back to false on successful submit. `pendingTab` holds the
  // intra-page tab the user is trying to switch to while the confirm modal
  // is open. In-flight submit is derived from the useMutation isLoading
  // flags below (see isSaveInFlight).
  const [isDirty, setIsDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  // Cross-route nav intent captured by history.block. When set, the modal
  // is shown; on confirm we unblock() and history.push(pendingNav) to let
  // the nav proceed. On cancel we just clear this and stay put.
  const [pendingNav, setPendingNav] = useState(null);
  // Server-side snapshot of the visit, captured on each successful useQuery /
  // refetch. Two invariants depend on this:
  //   1. On "leave anyway" (tab switch or router nav) we restore
  //      `initialValues` + shared allergies from this snapshot so the tab the
  //      user abandons doesn't look saved when they come back.
  //   2. `handleAssesmentSubmit` (nurse/pharmacy) sources doctor fields from
  //      this ref instead of the mutable `initialValues` state, so a nurse
  //      save can never ship live-typed-but-unsaved doctor text.
  // Read-only after each onSuccess; do NOT mutate on keystroke.
  const serverSnapshotRef = useRef({});

  const [rawAssessmentData, setRawAssessmentData] = useState([]);

  const [vitalSignData, setVitalSignData] = useState({
    beratBadan: "",
    tinggiBadan: "",
    suhu: "",
    satOksigen: "",
    tds: "",
    tdd: "",
    frekuensiPernapasan: "",
    frekuensiNadi: "",
    updatedAt: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const isView = isViewFromState && !isEditing;

  const loadOptions = async (inputValue, callback) => {
    if (inputValue) {
      const options = [];
      const { data } = await getDiagnosisOption({ keyword: inputValue });
      if (data && data.length > 0) {
        data.forEach((item) => {
          options.push({
            value: item.fullCode,
            label: item.shortDescription,
          });
        });
      }
      callback(options);
    }
  };

  const { refetch } = useQuery(
    ["ActiveVisitSingle", id],
    () => getActiveVisitSingle(id),
    {
      onSuccess: (res) => {
        const response = res.data;
        if (response) {
          setInitialValues({
            anamnesia: response.anamnesis,
            examination: response.examination,
            historyOfIllness: response?.riwayatPenyakit,
            familyHistoryOfDisease: response?.riwayatPenyakitKeluarga,
            diagnosis: response.diagnosisJson || [],
            allergies: response.allergies || [],
            fileUploads:
              (response?.fileUploads || []).filter((file) => file) || [],
            treatment: response.treatment,
            satuSehatEncounterId: response.satuSehatEncounterId,
            patientId: response?.patient?.id || parseInt(patientId),
            practitionerRole: response?.practitionerRole || [],
            routineMedications: response?.obatRutin || "",
            updatedBy:
              response?.lastModifiedBy || response?.practitionerName || "",
            updatedAt: response?.lastModifiedDate
              ? moment(response.lastModifiedDate).format("DD MMMM YYYY, HH:mm")
              : moment().format("DD MMMM YYYY, HH:mm"),
          });

          // Capture the immutable server snapshot. Used by (a) leave-anyway
          // restore and (b) nurse/pharmacy submit to prevent shipping
          // unsaved doctor edits.
          serverSnapshotRef.current = {
            anamnesia: response.anamnesis,
            historyOfIllness: response?.riwayatPenyakit,
            familyHistoryOfDisease: response?.riwayatPenyakitKeluarga,
            diagnosis: response.diagnosisJson || [],
            allergies: response.allergies || [],
            fileUploads:
              (response?.fileUploads || []).filter((file) => file) || [],
            treatment: response.treatment,
            routineMedications: response?.obatRutin || "",
            examination: response.examination,
          };

          const doctorAssessmentData = {
            id: `doctor-${response.id}`,
            patient_id: response?.patient?.id || parseInt(patientId),
            staff_id: response?.practitionerId || null,
            visit_id: parseInt(id),
            role: "doctor",
            subjective: response.anamnesis || "",
            objective: response.examination || "",
            assessment: response.diagnosisJson
              ? JSON.stringify(response.diagnosisJson)
              : "",
            plan: response.treatment || "",
            created_at:
              response.createdAt || response.start || new Date().toISOString(),
            updated_at:
              response.lastModifiedDate ||
              response.start ||
              new Date().toISOString(),
            created_by: response?.practitionerName || "System",
            updated_by:
              response?.lastModifiedBy ||
              response?.practitionerName ||
              "System",
          };

          setRawAssessmentData((prevData) => {
            const filteredData = prevData.filter(
              (item) => item.role !== "doctor"
            );
            return [doctorAssessmentData, ...filteredData];
          });

          const sharedAssessmentData = {
            allergies: response.allergies || [],
            historyOfIllness: response?.riwayatPenyakit || "",
            familyHistoryOfDisease: response?.riwayatPenyakitKeluarga || "",
            routineMedications: response?.obatRutin || "",
            fileUploads:
              (response?.fileUploads || []).filter((file) => file) || [],
          };

          setAssesmentFormData((prevState) => ({
            nurse: { ...prevState.nurse, ...sharedAssessmentData },
            pharmacy: { ...prevState.pharmacy, ...sharedAssessmentData },
          }));
        }

        setAnnotationData(res?.data?.annotationJson || []);
        const gender = extractPatientGender(res?.data);
        if (gender) {
          setPatientGender(gender);
        }

        if (response) {
          setDetailPatient({
            id: response?.patient?.id || parseInt(patientId),
            name: response.patientName,
            dob: response.dateOfBirth,
            email: response.patientEmail,
            phoneNumber: response.patientPhoneNumber,
            gender: response.gender,
            allergy: response.allergies,
            satuSehatPatientId: response.satuSehatPatientId,
            medicalRecordNumber: response.medicalRecordNumber,
            identificationNumber: response.identificationNumber,
            identificationType: response.identificationType,
            lastModifiedDate: response.patientLastModifiedDate,
            lastModifiedBy: response.patientLastModifiedBy,
          });
        }
      },
      refetchOnWindowFocus: false,
    }
  );

  const { isLoading: isLoadingSave, mutate: mutateSaveVisit, mutateAsync: mutateSaveVisitAsync } = useMutation(
    postSaveVisit,
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          setIsDirty(false);
          refetch();
          notification.success(localize.getText("successAssesment"));
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: (error) => {
        notification.error(
          error?.response?.data?.detail || localize.getText("unknownMessageError")
        );
      },
    }
  );

  const { isLoading: isLoadingUpdateSave, mutate: mutateUpdateVisit } =
    useMutation(postUpdateVisit, {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          setIsDirty(false);
          refetch();
          notification.success(localize.getText("successAssesment"));
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: (error) => {
        notification.error(
          error?.response?.data?.detail || localize.getText("unknownMessageError")
        );
      },
    });

  const {
    isLoading: isLoadingUpdateStatus,
    mutate: mutateUpdateReservationStatus,
  } = useMutation(putUpdateReservationStatus, {
    onSuccess: (res) => {
      if (res.status === 200 || res.status === 201) {
        refetch();
        refetchPharmacyCount();
        notification.success(localize.getText("successAssesment"));
      } else {
        notification.error(
          res?.data?.detail || localize.getText("unknownMessageError")
        );
      }
    },
    onError: (error) => {
      notification.error(
        error?.response?.data?.detail || localize.getText("unknownMessageError")
      );
    },
  });

  const { isLoading: isLoadingSaveNurse, mutate: mutateSaveAssesment, mutateAsync: mutateSaveAssesmentAsync } =
    useMutation(postAssessment, {
      onSuccess: async (res) => {
        if (res.status === 200 || res.status === 201) {
          setIsDirty(false);
          await saveVitalSigns();

          if (isNurse) {
            mutateUpdateReservationStatus({
              status: statusReservationType.REQUEST_MEDICINE,
              booking_id: null,
              visit_id: id,
            });
          } else {
            refetch();
          }

          refetchAssessment();
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: (error) => {
        notification.error(
          error?.response?.data?.detail || localize.getText("unknownMessageError")
        );
      },
    });

  const { isLoading: isLoadingSaveVitalSigns, mutate: mutateSaveVitalSigns, mutateAsync: mutateSaveVitalSignsAsync } =
    useMutation(postVitalSigns, {
      onSuccess: (res) => {
        refetchVitalSigns();
      },
      onError: (error) => {
        notification.error(
          error?.response?.data?.detail || localize.getText("unknownMessageError")
        );
      },
    });

  const { isLoading: isLoadingVitalSigns, refetch: refetchVitalSigns } =
    useQuery(
      ["VitalSigns", id, patientId],
      () => getVitalSigns(id, patientId),
      {
        onSuccess: (res) => {
          if (res?.data?.data) {
            const vitalData = res.data.data;

            setVitalSignData({
              beratBadan: vitalData.weight_kg?.toString() || "",
              tinggiBadan: vitalData.height_cm?.toString() || "",
              suhu: vitalData.temperature_celsius?.toString() || "",
              satOksigen: vitalData.oxygen_saturation_percent?.toString() || "",
              tds: vitalData.systolic_bp_mmhg?.toString() || "",
              tdd: vitalData.diastolic_bp_mmhg?.toString() || "",
              frekuensiPernapasan:
                vitalData.respiratory_rate_per_min?.toString() || "",
              frekuensiNadi: vitalData.pulse_rate_per_min?.toString() || "",
              updatedAt: vitalData.updated_at
                ? moment(vitalData.updated_at).format("DD MMMM YYYY, HH:mm")
                : "",
            });
          }
        },
        onError: (res) => {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        },
        refetchOnWindowFocus: false,
        enabled: !!id && !!patientId,
      }
    );

  const { isLoading: isLoadingAssessment, refetch: refetchAssessment } =
    useQuery(
      ["AssessmentProgressNotes", id, patientId],
      () => getAssessment(id, patientId),
      {
        onSuccess: (res) => {
          if (res?.data?.data && Array.isArray(res.data.data)) {
            const assessments = res.data.data;

            setRawAssessmentData((prevState) => [...prevState, ...assessments]);

            const roleData = {
              nurse: { ...defaultAssesmentState },
              pharmacy: { ...defaultAssesmentState },
            };

            assessments.forEach((assessment) => {
              const role = assessment.role?.toLowerCase();

              if (role === "nurse" || role === "pharmacy") {
                const formattedUpdatedAt = assessment.updated_at
                  ? moment(
                      assessment.updated_at,
                      "YYYY-MM-DDTHH:mm:ss.SSSZ"
                    ).format("DD MMMM YYYY, HH:mm")
                  : "";

                roleData[role] = {
                  ...roleData[role],
                  subjective: assessment.subjective || "",
                  objective: assessment.objective || "",
                  assessment: assessment.assessment || "",
                  plan: assessment.plan || "",
                  updatedBy: assessment.updated_by || "",
                  updatedAt: formattedUpdatedAt,
                  allergies: assesmentFormData[role]?.allergies || [],
                  historyOfIllness:
                    assesmentFormData[role]?.historyOfIllness || "",
                  familyHistoryOfDisease:
                    assesmentFormData[role]?.familyHistoryOfDisease || "",
                  routineMedications:
                    assesmentFormData[role]?.routineMedications || "",
                  fileUploads: assesmentFormData[role]?.fileUploads || [],
                };
              }
            });

            setAssesmentFormData(roleData);
          }
        },
        onError: (error) => {
          console.log("DY: No assessment data found or error:", error);
        },
        refetchOnWindowFocus: false,
        enabled: !!id && !!patientId,
      }
    );

  useEffect(() => {
    const titlePage = document.getElementById("title-page");
    if (state?.isView) {
      titlePage.innerText = "Visit";
    }
  }, [state]);

  const handleOnChange = (name, value) => {
    setIsDirty(true);
    setInitialValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === "allergies") {
      setAssesmentFormData((prevState) => ({
        nurse: {
          ...prevState.nurse,
          allergies: value,
        },
        pharmacy: {
          ...prevState.pharmacy,
          allergies: value,
        },
      }));
    }
  };

  const handleAssesmentOnChange = (roleType, name, value) => {
    setIsDirty(true);
    setAssesmentFormData((prevState) => ({
      ...prevState,
      [roleType]: {
        ...prevState[roleType],
        [name]: value,
      },
    }));
  };

  const handleSubmitForm = async (value) => {
    const fileUploads = [];

    if (value?.fileUploads) {
      await Promise.all(
        Array.from(value.fileUploads).map(async (file) => {
          let imageUrl = file;
          if (file && file.constructor === File) {
            imageUrl = await retrieveUrlFile(file);
          }
          fileUploads.push(imageUrl);
        })
      );
    }

    const diagnosisDTOs = value.diagnosis.map((item) => ({
      ...item,
      ...(item.__isNew__ ? {} : { __isNew__: false }),
    }));

    const alergiesDTOs = value.allergies.map((item) => ({
      ...item,
      ...(item.__isNew__ ? {} : { __isNew__: false }),
    }));

    const data = {
      anamnesis: value.anamnesia,
      examination: value.examination,
      treatment: value.treatment,
      riwayatPenyakit: value.historyOfIllness,
      riwayatPenyakitKeluarga: value.familyHistoryOfDisease,
      obatRutin: value.routineMedications,
      diagnosisDTOs,
      allergies: alergiesDTOs,
      annotationDTOs: value.annotationDTOs || [],
      fileUploads: fileUploads.filter((item) => item),
      patientId: patientId || null,
    };

    if (isEditing) {
      mutateUpdateVisit({ ...data, visitId: id });
    } else {
      mutateSaveVisit(data);
    }
  };

  const handleAssesmentSubmit = async (value) => {
    try {
      const assesmentData = assesmentFormData[activeMenuAssesment];
      const fileUploads = [];

      if (assesmentData?.fileUploads) {
        await Promise.all(
          Array.from(assesmentData.fileUploads).map(async (file) => {
            let imageUrl = file;
            if (file && file.constructor === File) {
              imageUrl = await retrieveUrlFile(file);
            }
            fileUploads.push(imageUrl);
          })
        );
      }

      const alergiesDTOs = (assesmentData.allergies || []).map((item) => ({
        ...item,
        ...(item.__isNew__ ? {} : { __isNew__: false }),
      }));

      const summary = annotationData.flatMap((annotation) =>
        annotation.annotationDetails.map((detail) => detail.description)
      );
      const examitation = annotationData.length > 0 ? summary.join(", ") : "";

      const doctorData = {
        riwayatPenyakit: assesmentData.historyOfIllness || "",
        riwayatPenyakitKeluarga: assesmentData.familyHistoryOfDisease || "",
        obatRutin: assesmentData.routineMedications || "",
        allergies: alergiesDTOs,
        fileUploads: fileUploads.filter((item) => item),
        patientId: patientId || null,
        // Keep existing values for other fields — sourced from the immutable
        // server snapshot, NOT the mutable `initialValues` state, so a nurse
        // save can never ship live-typed doctor edits that haven't been saved.
        anamnesis: serverSnapshotRef.current.anamnesia || "",
        examination: examitation,
        treatment: serverSnapshotRef.current.treatment || "",
        diagnosisDTOs: serverSnapshotRef.current.diagnosis || [],
        annotationDTOs: annotationData,
      };

      // CRITICAL: use mutateAsync so an exception from the Java save aborts
      // the chain. With plain mutate(), the call is fire-and-forget — Lambda
      // assessment-save would fire even when the Java visit save failed,
      // producing orphaned assessments and the silent data-loss behavior
      // reported by Dr. Wigati (Ibu Ita / Ibu Jesslyn).
      await mutateSaveVisitAsync(doctorData);

      const assessmentPayload = {
        patient_id: parseInt(patientId),
        visit_id: parseInt(id),
        staff_id: userDetail?.id,
        role: activeMenuAssesment,
        subjective: assesmentData.subjective || "",
        objective: assesmentData.objective || "",
        assessment: assesmentData.assessment || "",
        plan: assesmentData.plan || "",
        created_by: userDetail?.firstName,
        updated_by: userDetail?.firstName,
      };

      if (activeMenuAssesment === "nurse") {
        await saveVitalSigns();
      }
      await mutateSaveAssesmentAsync(assessmentPayload);
    } catch (error) {
      // Per-mutation onError already surfaced the toast with the backend
      // `detail` — don't fire a second generic one here. Just log for
      // debugging.
      console.error("Error in nurse form submission:", error);
    }
  };

  const saveVitalSigns = async () => {
    const vitalSignsPayload = {
      patient_id: parseInt(patientId),
      visit_id: parseInt(id),
      weight_kg: parseFloat(vitalSignData.beratBadan) || null,
      height_cm: parseFloat(vitalSignData.tinggiBadan) || null,
      temperature_celsius: parseFloat(vitalSignData.suhu) || null,
      oxygen_saturation_percent: parseFloat(vitalSignData.satOksigen) || null,
      systolic_bp_mmhg: parseFloat(vitalSignData.tds) || null,
      diastolic_bp_mmhg: parseFloat(vitalSignData.tdd) || null,
      respiratory_rate_per_min:
        parseFloat(vitalSignData.frekuensiPernapasan) || null,
      pulse_rate_per_min: parseFloat(vitalSignData.frekuensiNadi) || null,
      created_by: userDetail?.firstName,
      updated_by: userDetail?.firstName,
    };

    // Throw on failure so the calling handleAssesmentSubmit's try/catch can
    // abort the chain and surface a toast — prevents silent vital-signs loss.
    await mutateSaveVitalSignsAsync(vitalSignsPayload);
  };

  const handleVitalSignChange = (key, value) => {
    setIsDirty(true);
    setVitalSignData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    refetch();
    refetchAssessment();
    refetchVitalSigns();
  };

  const RenderActionButton = () => {
    return (
      <div className="d-flex justify-content-between mt-3 flex-wrap gap-3">
        <div className="d-flex gap-3">
          {isEditing ? (
            <>
              <Button
                label={localize.getText("cancel")}
                variant="outline-primary"
                onClick={handleCancelEdit}
              />
              <Button label={localize.getText("save")} type="submit" />
            </>
          ) : (
            <Button
              label={localize.getText("back")}
              variant="outline-primary"
              onClick={() => history.goBack()}
            />
          )}

          {!isView && !isEditing && (
            <Button label={localize.getText("save")} type="submit" />
          )}
        </div>
        <Link
          to={{
            pathname: "/history",
            search: `?id=${initialValues.patientId}`,
          }}
        >
          <Button label={localize.getText("history")} variant="info" />
        </Link>
      </div>
    );
  };

  const getAllMenuItems = () => [
    {
      label: "Dokter",
      value: "doctor",
      content: (
        <DoctorAssesment
          RenderActionButton={RenderActionButton}
          handleSubmitForm={handleSubmitForm}
          initialValues={initialValues}
          isDoctor={true}
          isNurse={false}
          isView={isView}
          handleOnChange={handleOnChange}
          loadOptions={loadOptions}
          annotationJson={annotationData}
          patientGender={patientGender}
          onEditClick={handleEditClick}
        />
      ),
    },
    {
      label: "Perawat/Terapis",
      value: "nurse",
      content: (
        <NurseAssesment
          RenderActionButton={RenderActionButton}
          handleSubmitForm={handleAssesmentSubmit}
          initialValues={assesmentFormData.nurse}
          vitalSignData={vitalSignData}
          onVitalSignChange={handleVitalSignChange}
          isView={isView}
          handleOnChange={(name, value) =>
            handleAssesmentOnChange("nurse", name, value)
          }
          loadOptions={loadOptions}
          onEditClick={handleEditClick}
        />
      ),
    },
    {
      label: "Farmasi",
      value: "pharmacy",
      content: (
        <PharmacyAssesment
          RenderActionButton={RenderActionButton}
          handleSubmitForm={handleAssesmentSubmit}
          initialValues={assesmentFormData.pharmacy}
          isView={isView}
          handleOnChange={(name, value) =>
            handleAssesmentOnChange("pharmacy", name, value)
          }
          loadOptions={loadOptions}
          onEditClick={handleEditClick}
        />
      ),
    },
    {
      label: "CPPT",
      value: "cppt",
      content: <CpptSummary assessmentData={rawAssessmentData} />,
    },
  ];

  const getFilteredMenuItems = () => {
    const allMenuItems = getAllMenuItems();

    if (isOwner) {
      return allMenuItems;
    }

    if (isDoctor) {
      return allMenuItems.filter(
        (item) => item.value === "doctor" || item.value === "cppt"
      );
    }

    if (isNurse) {
      return allMenuItems.filter(
        (item) => item.value === "nurse" || item.value === "cppt"
      );
    }

    if (isPharmacy) {
      return allMenuItems.filter(
        (item) => item.value === "pharmacy" || item.value === "cppt"
      );
    }

    return allMenuItems.filter((item) => item.value === "cppt");
  };

  const menuAssesment = getFilteredMenuItems();

  const handleTabChange = (newTab) => {
    const hasPermission = menuAssesment.some((menu) => menu.value === newTab);

    if (!hasPermission) {
      console.warn(
        `User with role ${role} doesn't have permission to access ${newTab} tab`
      );
      return;
    }

    if (newTab === activeMenuAssesment) return;

    // Intra-page tab switch bypasses react-router, so <Prompt> can't guard
    // it. Show our custom modal instead. If nothing is dirty, switch
    // immediately.
    if (isDirty) {
      setPendingTab(newTab);
      return;
    }

    setActiveMenuAssesment(newTab);
  };

  // Discard local edits and re-seed all form state from the server.
  // Called on "leave anyway" so the abandoned tab (any tab) doesn't look
  // saved when the user comes back. Refetching lets the existing onSuccess
  // handlers rebuild initialValues + assesmentFormData for all three roles,
  // which is more robust than maintaining a hand-rolled snapshot mirror of
  // every field across three queries.
  const restoreServerSnapshot = () => {
    refetch();
    refetchVitalSigns();
    refetchAssessment();
  };

  const confirmLeaveTab = () => {
    if (!pendingTab) return;
    // User chose to discard: restore server snapshot, drop dirty flag, then
    // flip tabs.
    restoreServerSnapshot();
    setIsDirty(false);
    setActiveMenuAssesment(pendingTab);
    setPendingTab(null);
  };

  const cancelLeaveTab = () => setPendingTab(null);

  // Hard-navigation guard (tab close / reload / external nav).
  // Only attached while dirty and not mid-submit — attaching always is a
  // perf/lint smell and interferes with bfcache.
  const isSaveInFlight =
    isLoadingSave ||
    isLoadingUpdateSave ||
    isLoadingSaveNurse ||
    isLoadingSaveVitalSigns;
  useBeforeUnload(isDirty && !isSaveInFlight);

  // Router-level guard via history.block (replaces <Prompt> so we can render
  // our own Bahasa modal instead of the OS-native confirm dialog).
  //
  // Gotchas we handle:
  //   1. Stale closure over isDirty/isSaveInFlight — refs give the block
  //      callback current values without re-registering on every keystroke.
  //   2. Same-pathname nav (query-string tweaks) should pass through.
  //   3. Cleanup unblocks on unmount so a stale blocker can't strand nav.
  //   4. On confirm we must call unblock() BEFORE history.push, otherwise
  //      the push re-triggers the same blocker in an infinite loop.
  const isDirtyRef = useRef(isDirty);
  const inFlightRef = useRef(isSaveInFlight);
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);
  useEffect(() => {
    inFlightRef.current = isSaveInFlight;
  }, [isSaveInFlight]);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const unblockRef = useRef(null);
  useEffect(() => {
    const unblock = history.block((nextLocation) => {
      if (!isDirtyRef.current || inFlightRef.current) return true;
      if (nextLocation.pathname === pathnameRef.current) return true;
      setPendingNav({
        pathname: nextLocation.pathname,
        search: nextLocation.search,
        hash: nextLocation.hash,
        state: nextLocation.state,
      });
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      unblock();
      unblockRef.current = null;
    };
    // history reference is stable; we intentionally register once and read
    // current state via refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmLeaveNav = () => {
    if (!pendingNav) return;
    restoreServerSnapshot();
    setIsDirty(false);
    // Unblock BEFORE push, otherwise the push itself hits the blocker again.
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    const target = pendingNav;
    setPendingNav(null);
    history.push(target);
  };
  const cancelLeaveNav = () => setPendingNav(null);

  // One modal, two triggers: intra-page tab switch OR cross-route nav.
  const modalVisible = pendingTab !== null || pendingNav !== null;
  const handleModalCancel = () => {
    if (pendingNav) cancelLeaveNav();
    else cancelLeaveTab();
  };
  const handleModalLeave = () => {
    if (pendingNav) confirmLeaveNav();
    else confirmLeaveTab();
  };

  return (
    <Spinner
      loading={
        isLoadingSave ||
        isLoadingUpdateStatus ||
        isLoadingSaveNurse ||
        isLoadingSaveVitalSigns ||
        isLoadingVitalSigns ||
        isLoadingAssessment ||
        isLoadingUpdateSave
      }
    >
      <UnsavedChangesModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        onLeave={handleModalLeave}
      />
      <div className="card">
        <div className="card-body gap-4 d-flex flex-column">
          <TitlePage text={localize.getText("back")} isSmall isBack />
          <PatientDataCard
            isAssesment
            isHistoryPatient
            isActive={isMobile}
            isVitalSign={
              activeMenuAssesment === "doctor" ||
              activeMenuAssesment === "pharmacy"
            }
            onClickVitalSign={() => setShowVitalSign(true)}
            detailPatient={detailPatient}
            onClickDetail={() =>
              history.push({
                pathname: "/history",
                search: `?id=${patientId}`,
              })
            }
          />
          <TabMenu
            items={menuAssesment}
            activeTab={activeMenuAssesment}
            onTabChange={handleTabChange}
          />
        </div>
      </div>

      <Modal
        visible={showVitalSign}
        size="lg"
        handleHideModal={() => setShowVitalSign(false)}
      >
        <VitalSign formData={vitalSignData} isView />
      </Modal>
    </Spinner>
  );
}
